import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import type { PageTableNavigate } from '@/hooks/use-page-table.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import type { CustomerQuery, CustomerRow } from '../customer-list-types.ts'

export function useCustomerListActions({
  query,
  rows,
  navigate
}: {
  query: CustomerQuery
  rows: CustomerRow[] | undefined
  navigate: PageTableNavigate<CustomerQuery>
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message, modal } = App.useApp()
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<number[]>([])

  const setDisabled = useMutation(trpc.customer.setDisabled.mutationOptions())

  const batchSetDisabled = useMutation(trpc.customer.batchSetDisabled.mutationOptions())

  const removeCustomerMutation = useMutation(trpc.customer.remove.mutationOptions())

  const batchClearTags = useMutation(trpc.customer.batchClearTags.mutationOptions())

  const batchRemove = useMutation(trpc.customer.batchRemove.mutationOptions())

  const invalidateList = () =>
    Promise.all([
      queryClient.invalidateQueries(trpc.customer.list.queryFilter()),
      queryClient.invalidateQueries(trpc.customer.billingCalendar.queryFilter())
    ])

  const changeStatus = async (customer: CustomerRow) => {
    try {
      await setDisabled.mutateAsync({ id: customer.id, isDisabled: !customer.isDisable })
      void message.success(customer.isDisable ? '客户已恢复' : '客户已停用')
      await invalidateList()
    } catch (error) {
      void message.error(getErrorMessage(error, '客户状态更新失败'))
    }
  }

  const changeSelectedStatus = async (isDisabled: boolean) => {
    try {
      const result = await batchSetDisabled.mutateAsync({ ids: selectedCustomerIds, isDisabled })
      setSelectedCustomerIds([])
      void message.success(`已${isDisabled ? '停用' : '恢复'} ${result.count} 个客户`)
      await invalidateList()
    } catch (error) {
      void message.error(getErrorMessage(error, '批量更新客户状态失败'))
    }
  }

  const removeCustomer = async (customer: CustomerRow) => {
    try {
      await removeCustomerMutation.mutateAsync({ id: customer.id })
      void message.success('客户已删除')
      if (rows?.length === 1 && query.current > 1) {
        await navigate({ search: (previous) => ({ ...previous, current: query.current - 1 }) })
      } else {
        await invalidateList()
      }
    } catch (error) {
      void message.error(getErrorMessage(error, '客户删除失败'))
    }
  }

  const confirmChangeStatus = (customer: CustomerRow) => {
    modal.confirm({
      content: customer.isDisable ? '恢复后可继续用于业务。' : '停用后历史数据仍会保留。',
      okButtonProps: customer.isDisable ? undefined : { danger: true },
      onOk: () => changeStatus(customer),
      title: customer.isDisable ? '确认恢复客户？' : '确认停用客户？'
    })
  }

  const confirmRemoveCustomer = (customer: CustomerRow) => {
    modal.confirm({
      content: '只有已停用客户可以删除；删除采用软删除，历史关联数据仍会保留。',
      okButtonProps: { danger: true },
      onOk: () => removeCustomer(customer),
      title: '确认删除该客户？'
    })
  }

  const removeSelectedCustomers = async () => {
    try {
      const result = await batchRemove.mutateAsync({ ids: selectedCustomerIds })
      const allVisibleRowsSelected =
        selectedCustomerIds.length === (rows?.length ?? 0) && selectedCustomerIds.length > 0
      setSelectedCustomerIds([])
      const resultMessage = `删除完成：成功 ${result.successCount} 个，失败 ${result.failedCount} 个`
      if (result.failedCount === 0) {
        void message.success(resultMessage)
      } else if (result.successCount === 0) {
        void message.error(resultMessage)
      } else {
        void message.warning(resultMessage)
      }
      if (result.failedCount === 0 && allVisibleRowsSelected && query.current > 1) {
        await navigate({ search: (previous) => ({ ...previous, current: query.current - 1 }) })
      } else {
        await invalidateList()
      }
    } catch (error) {
      void message.error(getErrorMessage(error, '批量删除客户失败'))
    }
  }

  const confirmChangeSelectedStatus = (isDisabled: boolean) => {
    if (!isDisabled) {
      void changeSelectedStatus(false)
      return
    }
    modal.confirm({
      content: '停用后，选中的客户不能继续用于新的业务操作。',
      okButtonProps: { danger: true },
      okText: '确认停用',
      onOk: () => changeSelectedStatus(true),
      title: `停用已选 ${selectedCustomerIds.length} 个客户？`
    })
  }

  const confirmRemoveSelectedCustomers = () => {
    modal.confirm({
      content:
        '系统会逐条尝试删除。只有已停用客户可以删除；删除采用软删除，历史关联数据仍会保留。不符合条件的客户会计为失败。',
      okButtonProps: { danger: true },
      okText: '确认删除',
      onOk: removeSelectedCustomers,
      title: `删除已选 ${selectedCustomerIds.length} 个客户？`
    })
  }

  const confirmClearSelectedTags = () => {
    const ids = [...selectedCustomerIds]
    modal.confirm({
      title: `清空已选 ${ids.length} 个客户的标签？`,
      content: '将移除所选客户的全部标签关联，客户标签目录及其他客户的标签不受影响。',
      okText: '确认清空',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const result = await batchClearTags.mutateAsync({ ids })
          setSelectedCustomerIds([])
          void message.success(`已清空 ${result.count} 个客户的标签`)
          await invalidateList()
          if (query.current > 1) {
            await navigate({ search: (previous) => ({ ...previous, current: 1 }) })
          }
        } catch (error) {
          void message.error(getErrorMessage(error, '清空客户标签失败'))
          throw error
        }
      }
    })
  }
  return {
    selectedCustomerIds,
    setSelectedCustomerIds,
    invalidateList,
    confirmChangeStatus,
    confirmRemoveCustomer,
    confirmChangeSelectedStatus,
    confirmRemoveSelectedCustomers,
    confirmClearSelectedTags,
    batchRemove,
    batchClearTags,
    batchSetDisabled
  }
}
